// The category control drawn throughout the feed/inbox/detail: TWO
// separately bordered, separately radiused tags -- a scope tag
// (Personal/Business/Transfer/Not set) and a type tag (the leaf name, or
// Categorize/Unknown type/Loading/Types unavailable) -- per the 2026-09-08
// redesign spec §3 ("Category control specification"). No shared pill
// enclosure or joined segmented border; 8px empty space between them.
//
// The categorization `source` never reaches the DOM as an attribute -- it
// only decides whether the user-set square mark is drawn (merge of
// e2e_wire's canonical/honest-state UI with PR #22: AGENTS.md Data & UI
// honesty 4, asserted by FeedRow's canonical no-methodology test and
// TransactionDetailPanel's private-DOM test). Confidence itself stays on
// the wire (LineItemOut, write_audit) but is never rendered by this
// component -- there is no `confidence` prop at all, so passing one is a
// type error, not a silently ignored value.
//
// Founder feedback 2026-09-08 ("personal/business and type are two
// different tags. they should have a clear boundary for separation") and
// the redesign spec's exact token/size table: scope is `rounded-full`,
// 12px horizontal padding, 13px/18px text (14px/20px in `size="detail"`);
// type is `rounded-md`, 10px horizontal padding, same text sizes. Both are
// 44px actual interactive height (not just an invisible tap-target
// overlay), never overlapping the other's hit area. Business uses the
// purple `business-*` tokens (not grey `primary/15`); Personal/Transfer use
// `muted`. Icons: 14px UserRound/BriefcaseBusiness/ArrowLeftRight for scope
// (6px label gap), 14px ChevronDown for type (6px gap, decorative
// `aria-hidden`).
//
// A user-set square mark (`StatusDot`) sits inside the TYPE tag before its
// label (spec §3 Provenance row) -- a provenance mark, never a confidence
// display (D5: "do not show the low confidence label"). Confidence itself
// stays on the wire but is never rendered here -- there is no `confidence`
// prop.
//
// Founder revision §14 decision 3 (2026-09-08) supersedes D-A above:
// tapping the scope tag OPENS A SMALL ANCHORED MENU (radio Personal/Business
// + an "Apply to all items from {merchant}" checkbox) instead of flipping
// instantly -- the caller (FeedRow/TransactionDetailPanel, via
// `onSelectScope`/`scopeFlip.flipTarget`) owns the PATCH once a scope is
// chosen from the menu. D-C (unchanged): tapping the type tag opens the
// `TypeChooser` popover/sheet. This component only renders the tap targets
// and reports taps via `onSelectScope`/`onOpenTypeChooser`.
//
// `slug` is read ONLY to derive which scope the leaf belongs to and is
// never rendered as text -- internal methodology (raw slugs) must not reach
// the DOM (AGENTS.md Data & UI honesty 4).
import { forwardRef } from 'react';
// This checkout's pinned `lucide-react` predates the `BriefcaseBusiness`/
// `UserRound` names the spec's icon table uses -- `Briefcase`/`User` are
// this version's equivalents (same glyphs, older icon-set naming).
import { Briefcase, ChevronDown, Loader2, User, ArrowLeftRight } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@moneyskyline/ui';
import { StatusDot } from './StatusDot';
import { scopeOfSlug, type CategoryScope } from '@/features/transactions/scopeFlip';

export type CategorySource = 'user' | 'rule' | 'ai' | 'import';
export type { CategoryScope };

const SCOPE_LABEL: Record<CategoryScope, string> = {
  personal: 'Personal',
  business: 'Business',
  transfer: 'Transfer',
};

// `scopeOfSlug` is the single source of truth in `scopeFlip.ts` -- re-export
// it here so existing importers of `CategoryChip` keep working without a
// second, drifting copy (Tester R1 deferrable 1).
export { scopeOfSlug };

const GROUP_BASE = 'max-w-full items-center gap-2';
const SCOPE_BASE =
  'ms-tap-44 inline-flex min-h-11 shrink-0 items-center gap-1.5 rounded-full border px-3 text-[13px]/[18px] font-medium';
const SCOPE_BASE_DETAIL = 'text-[14px]/[20px]';
const TYPE_BASE =
  'ms-tap-44 inline-flex min-h-11 max-w-full items-center gap-1.5 rounded-md border px-2.5 text-left text-[13px]/[18px] font-medium sm:max-w-[240px]';
const TYPE_BASE_DETAIL = 'text-[14px]/[20px] sm:max-w-none sm:whitespace-normal';

// Personal / neutral scope tone (muted surface).
const TONE_MUTED = 'border-muted-foreground bg-muted text-foreground hover:bg-muted/70';
const TONE_MUTED_STATIC = 'border-muted-foreground bg-muted text-foreground';
// Business scope tone (purple business-* tokens, never grey primary/15).
const TONE_BUSINESS = 'border-business bg-business-surface text-business-foreground hover:bg-business-surface/70';
const TONE_BUSINESS_STATIC = 'border-business bg-business-surface text-business-foreground';
// Neutral card-surface type tone (used for a resolved leaf's type tag).
const TONE_CARD = 'border-muted-foreground bg-card text-foreground hover:bg-muted/50';
const TONE_CARD_STATIC = 'border-muted-foreground bg-card text-muted-foreground';
// Actionable-unknown tone (Categorize / Unknown type) -- uses the link token.
const TONE_LINK = 'border-link bg-card text-link hover:bg-link-surface';
// Degraded/failed tone (Types unavailable).
const TONE_DEGRADED = 'border-degraded-border bg-degraded-surface text-degraded-foreground';

export const CategoryChip = forwardRef<HTMLButtonElement, {
  path: string[];
  source: CategorySource;
  /** The leaf category's slug -- used only to resolve the scope tag; never rendered. */
  slug?: string | null;
  /**
   * The caller's already-resolved scope (ENG-R1-001: derived from the
   * category ROW's `kind`/`is_business` via `scopeOfCategory`, which
   * handles a custom leaf's unprefixed slug correctly). When omitted (or
   * `undefined`), falls back to `scopeOfSlug(slug)` for callers that don't
   * have the row handy.
   */
  scope?: CategoryScope | null;
  /**
   * The leaf's human display name, resolved by the caller (e.g. via
   * `useCategories` looked up by `category_id`) -- when present, this wins
   * over any `path`-derived label. Callers whose `path` is not itself a
   * genuine display path (e.g. a raw underscore-delimited slug) MUST pass
   * this instead of relying on `path` -- an internal slug must never reach
   * the DOM (AGENTS.md Data & UI honesty 4, ENG-R1-004/DES-R1-001).
   */
  typeLabel?: string | null;
  /** The leaf's parent name, shown under the group in `size="detail"` ("Business expense"). */
  parentLabel?: string | null;
  /** True while the caller's name lookup (e.g. `useCategories`) is still loading and no `typeLabel` is available yet -- renders a neutral skeleton instead of guessing from `path`. */
  typeLabelPending?: boolean;
  /** Whole taxonomy failed to load -- "Types unavailable" + Retry, read-only scope. */
  categoriesFailed?: boolean;
  onRetryCategories?: () => void;
  /**
   * §14 decision 3: tapping the scope pill opens an anchored menu (radio
   * Personal/Business + an "Apply to all items from {merchant}" checkbox)
   * instead of flipping instantly. Present only where a scope correction is
   * wired (FeedRow/TransactionDetailPanel single-line-item case); absent
   * elsewhere keeps the scope pill static. Called with the OTHER scope when
   * the user picks it from the menu -- selecting the already-current scope
   * is a no-op the menu itself swallows.
   */
  onSelectScope?: (scope: CategoryScope) => void;
  /** The menu's "Apply to all items from {merchant}" checkbox state -- present only alongside `onSelectScope`. */
  applyToMerchantChecked?: boolean;
  onApplyToMerchantChange?: (checked: boolean) => void;
  /**
   * ENG-R1-001: the scope menu's real open/close transitions (open, Escape,
   * outside click, or a selection) -- the caller uses `false` transitions to
   * reset ITS OWN `applyToMerchantChecked` state for this surface, so a
   * checkbox ticked here and then dismissed without a write never leaks
   * into a later interaction (§14: "defaults off per interaction").
   */
  onScopeMenuOpenChange?: (open: boolean) => void;
  /** The real all-time eligible count from a PRIOR write on this item -- `null`/`undefined` shows a count-free label rather than fabricating one (§14 decision 3). */
  merchantApplyCount?: number | null;
  /** Opens the compact TypeChooser popover/sheet for the type pill (D-C). Absent keeps the type pill static. */
  onOpenTypeChooser?: () => void;
  /** True while this line item's category write is in flight -- spinner in the icon slot, both tags aria-disabled, "Saving…" status announced. */
  saving?: boolean;
  /** Reflects the type chooser's real open state (§8: `aria-expanded`) -- defaults to `false` when the caller doesn't control a chooser here. */
  typeChooserOpen?: boolean;
  /** The chooser surface's own `id` (§8: `aria-controls`) -- omitted (no attribute) when not yet known/rendered. */
  typeChooserId?: string;
  /** Static "Split · {count}" summary; no per-allocation write here. */
  split?: number;
  /** Static, muted -- removed/superseded line item; no writes. */
  removed?: boolean;
  /** Merchant display name, used only to build the group's accessible name ("Category for {merchant}"). */
  merchantName?: string | null;
  /** Spec §3: feed uses 13px/18px text; detail/chooser context uses 14px/20px and lets the type tag wrap instead of truncating. Defaults to `"feed"`. */
  size?: 'feed' | 'detail';
  /**
   * DES-F-001: below 720px, `FeedRow` needs scope · type · amount on ONE
   * non-wrapping line -- scope a fixed ~104px column, type `flex-1 min-w-0`
   * truncating with a `title`, amount handled by the caller. Only at a true
   * 320px zoom-level width does the type tag fall back to wrapping. Off by
   * default (unchanged flex-wrap behavior) for every other consumer
   * (detail, split, removed).
   */
  fixedLayout?: boolean;
  busy?: boolean;
  className?: string;
}>(function CategoryChip(
  {
    path,
    source,
    slug,
    scope: scopeOverride,
    typeLabel: typeLabelOverride,
    parentLabel,
    typeLabelPending,
    categoriesFailed,
    onRetryCategories,
    onSelectScope,
    applyToMerchantChecked,
    onApplyToMerchantChange,
    onScopeMenuOpenChange,
    merchantApplyCount,
    onOpenTypeChooser,
    saving,
    typeChooserOpen,
    typeChooserId,
    split,
    removed,
    merchantName,
    size = 'feed',
    fixedLayout,
    busy,
    className,
  },
  ref,
) {
  // DES-R2-002: at 200% text-only zoom (viewport still 390px, `html { font-size: 200% }`)
  // a `px`-based min-width scales visually but a fixed viewport-width media
  // query (`max-[320px]:flex-wrap`) never fires, since the VIEWPORT never
  // shrinks -- only the text does. Switching both floors to `rem` (so they
  // scale with the root font-size the same way the text does) and dropping
  // the nowrap/media-query pairing in favor of always-on `flex-wrap` means
  // the row wraps whenever the scope+type floors genuinely don't fit the
  // available width, at ANY zoom level, without a magic viewport breakpoint.
  // At 100% zoom the two floors comfortably fit FeedRow's fixed layout, so
  // DES-F-001 ("scope · type · amount on one line at 390px/100% text")
  // still holds -- wrapping only engages once zoom (or a narrower column)
  // pushes the combined floor past the available width.
  const scopeBase = [
    size === 'detail' ? [SCOPE_BASE, SCOPE_BASE_DETAIL].join(' ') : SCOPE_BASE,
    fixedLayout ? 'min-w-[6.5rem] justify-center' : '',
  ].filter(Boolean).join(' ');
  const typeBase = [
    size === 'detail' ? [TYPE_BASE, TYPE_BASE_DETAIL].join(' ') : TYPE_BASE,
    fixedLayout ? 'flex-1 min-w-[4.5rem]' : 'min-w-0',
  ].filter(Boolean).join(' ');
  const groupWrapClass = fixedLayout
    // The group's own box must not shrink below its widest single wrapped
    // line (the scope pill's floor) -- otherwise its children overflow a
    // collapsed parent box instead of the parent growing/wrapping to fit
    // them (flex children are not clipped to an undersized parent).
    ? 'flex w-full min-w-[6.5rem] flex-wrap'
    : 'inline-flex flex-wrap';
  const scope = scopeOverride != null ? scopeOverride : scopeOfSlug(slug);
  const fullPath = path.join(' › ');
  // With a resolved scope, the scope tag already names the parent bucket,
  // so the type tag shows just the leaf. Without one (no slug, or a slug
  // this app doesn't recognize), fall back to the full joined path -- the
  // pre-existing single-chip display. `typeLabelOverride` always wins when
  // present; `typeLabelPending` renders a neutral placeholder instead of a
  // guess while the caller's own lookup is still in flight.
  const derivedTypeLabel = scope !== null ? (path[path.length - 1] ?? fullPath) : fullPath;
  const typeLabel = typeLabelOverride ?? (typeLabelPending ? null : derivedTypeLabel);
  const isUncategorized = typeLabel === 'Uncategorized';
  const isUnknownType = typeLabel === 'Unknown category';
  const isBusy = !!(busy || saving);

  const otherScope: CategoryScope | null = scope === 'personal' ? 'business' : scope === 'business' ? 'personal' : null;

  const groupLabel = merchantName ? `Category for ${merchantName}` : 'Category';

  if (removed) {
    return (
      <div
        ref={undefined}
        role="group"
        aria-label={groupLabel}
        className={[groupWrapClass, GROUP_BASE, className].filter(Boolean).join(' ')}
        data-testid="category-chip"
        data-state="removed"
      >
        <span className={[scopeBase, 'border-border bg-transparent text-muted-foreground'].join(' ')} data-testid="category-chip-scope">
          {/* ENG-F-003: a removed row must render the REAL scope, or the
              honest "Not set" the live path uses -- never fabricate
              "Personal" for an item that was never actually scoped. */}
          {scope !== null ? SCOPE_LABEL[scope] : 'Not set'}
        </span>
        <span className={[typeBase, TONE_CARD_STATIC].join(' ')} data-testid="category-chip-type">
          {typeLabel ?? fullPath}
        </span>
      </div>
    );
  }

  if (typeof split === 'number') {
    return (
      <div
        role="group"
        aria-label={groupLabel}
        className={[groupWrapClass, GROUP_BASE, className].filter(Boolean).join(' ')}
        data-testid="category-chip"
        data-state="split"
      >
        <span
          data-testid="category-chip-split"
          className={[typeBase, TONE_CARD_STATIC].join(' ')}
        >
          Split · {split}
        </span>
      </div>
    );
  }

  let scopeTag: React.ReactNode = null;
  if (scope === 'transfer') {
    scopeTag = (
      <span
        data-testid="category-chip-scope"
        data-scope="transfer"
        className={[scopeBase, 'border-border bg-transparent text-muted-foreground'].join(' ')}
      >
        <ArrowLeftRight className="h-3.5 w-3.5" aria-hidden="true" />
        Transfer
      </span>
    );
  } else if (isUncategorized || (scope === null && !isUnknownType)) {
    scopeTag = (
      <span
        data-testid="category-chip-scope"
        data-scope="not_set"
        className={[scopeBase, 'border-border bg-transparent text-muted-foreground'].join(' ')}
      >
        Not set
      </span>
    );
  } else if (isUnknownType) {
    scopeTag = (
      <span
        data-testid="category-chip-scope"
        data-scope="not_set"
        className={[scopeBase, 'border-border bg-transparent text-muted-foreground'].join(' ')}
      >
        Not set
      </span>
    );
  } else if (scope !== null && otherScope !== null) {
    const tone = scope === 'business' ? TONE_BUSINESS : TONE_MUTED;
    const toneStatic = scope === 'business' ? TONE_BUSINESS_STATIC : TONE_MUTED_STATIC;
    const Icon = scope === 'business' ? Briefcase : User;
    if (onSelectScope) {
      // §14 decision 3: the scope pill opens a small anchored menu (radio
      // Personal/Business, current checked, plus an "Apply to all items
      // from {merchant}" checkbox) instead of flipping the scope the
      // instant it's tapped.
      // DATA-F-001: the headline never carries a count -- `N` is only ever
      // the ELIGIBLE count (excludes items the user already set themselves
      // and the item just corrected), so labeling it "all N" overstates it.
      // A known count instead renders a second, honest line.
      const applyLabel = merchantName
        ? `Apply to all items from ${merchantName}`
        : 'Apply to all items from this merchant';
      const applyEligibleCopy =
        merchantApplyCount != null
          ? `${merchantApplyCount} eligible · items you set yourself stay as they are`
          : null;
      scopeTag = (
        <DropdownMenu
          onOpenChange={(open) => {
            // ENG-F-002: never let a busy-state open register as a real
            // interaction (the pointerdown guard below should already have
            // stopped Radix from opening, but this is the second line of
            // defense if it ever does).
            if (isBusy && open) return;
            onScopeMenuOpenChange?.(open);
          }}
        >
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              data-testid="category-chip-scope"
              data-scope={scope}
              aria-label={`Scope: ${SCOPE_LABEL[scope]}. Change scope${merchantName ? ` for ${merchantName}` : ''}`}
              aria-disabled={isBusy}
              onPointerDown={(event) => {
                // ENG-F-002: Radix opens the menu on `pointerdown`, before
                // `onClick` ever fires -- guarding only `onClick` lets a
                // second scope select start mid-save. Block the open here.
                if (isBusy) event.preventDefault();
              }}
              onClick={(event) => {
                event.stopPropagation();
                if (isBusy) event.preventDefault();
              }}
              className={[scopeBase, tone, isBusy ? 'opacity-60' : ''].join(' ')}
            >
              <Icon className="h-3.5 w-3.5" aria-hidden="true" />
              {SCOPE_LABEL[scope]}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            data-testid="category-chip-scope-menu"
            aria-label={`Change scope${merchantName ? ` for ${merchantName}` : ''}`}
          >
            <DropdownMenuRadioGroup
              value={scope}
              onValueChange={(value) => {
                if (isBusy) return;
                if (value !== scope && (value === 'personal' || value === 'business')) {
                  onSelectScope(value);
                }
              }}
            >
              <DropdownMenuRadioItem value="personal" className="ms-tap-44 min-h-11" data-testid="category-chip-scope-option-personal">
                Personal
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="business" className="ms-tap-44 min-h-11" data-testid="category-chip-scope-option-business">
                Business
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
            {onApplyToMerchantChange && (
              <DropdownMenuCheckboxItem
                checked={!!applyToMerchantChecked}
                onCheckedChange={(checked) => onApplyToMerchantChange(checked === true)}
                onSelect={(event) => event.preventDefault()}
                className="ms-tap-44 min-h-11 whitespace-normal"
                data-testid="category-chip-apply-to-merchant"
              >
                <span className="flex flex-col">
                  <span>{applyLabel}</span>
                  {applyEligibleCopy && (
                    <span
                      className="text-xs text-muted-foreground"
                      data-testid="category-chip-apply-to-merchant-eligible"
                    >
                      {applyEligibleCopy}
                    </span>
                  )}
                </span>
              </DropdownMenuCheckboxItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    } else {
      scopeTag = (
        <span data-testid="category-chip-scope" data-scope={scope} className={[scopeBase, toneStatic].join(' ')}>
          <Icon className="h-3.5 w-3.5" aria-hidden="true" />
          {SCOPE_LABEL[scope]}
        </span>
      );
    }
  }

  let typeTag: React.ReactNode;
  if (categoriesFailed) {
    typeTag = (
      <span className={[typeBase, TONE_DEGRADED].join(' ')} data-testid="category-chip-type" data-state="failed">
        Types unavailable
        {onRetryCategories && (
          <button
            type="button"
            className="ms-tap-44 ml-1 underline"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onRetryCategories();
            }}
          >
            Retry
          </button>
        )}
      </span>
    );
  } else if (typeLabel === null) {
    // Loading and no resolved name yet: a neutral placeholder, never the
    // raw slug or a guessed label (AGENTS.md Data & UI honesty 4).
    typeTag = (
      <span
        data-testid="category-chip-type"
        data-pending="true"
        aria-hidden="true"
        className={[typeBase, TONE_CARD_STATIC].join(' ')}
      >
        …
      </span>
    );
  } else {
    const label = isUncategorized ? 'Categorize' : typeLabel;
    const tone = isUncategorized || isUnknownType ? TONE_LINK : TONE_CARD;
    const ariaLabel = isUncategorized
      ? `Categorize transaction${merchantName ? ` for ${merchantName}` : ''}`
      : `Change type: ${typeLabel}${parentLabel ? `, ${parentLabel}` : ''}${merchantName ? `, for ${merchantName}` : ''}`;
    if (onOpenTypeChooser) {
      typeTag = (
        <button
          ref={ref}
          type="button"
          data-testid="category-chip-type"
          title={fullPath}
          aria-label={ariaLabel}
          aria-haspopup="dialog"
          aria-expanded={!!typeChooserOpen}
          {...(typeChooserId ? { 'aria-controls': typeChooserId } : {})}
          aria-disabled={isBusy}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            // §3 "Saving correction": aria-disabled + a guarded handler,
            // never the `disabled` attribute -- keeps keyboard focus on
            // this tag through the write instead of yanking it away.
            if (isBusy) return;
            onOpenTypeChooser();
          }}
          onKeyDown={(event) => {
            if (isBusy && (event.key === 'Enter' || event.key === ' ')) {
              event.preventDefault();
            }
          }}
          className={[typeBase, tone, isBusy ? 'opacity-60' : ''].join(' ')}
        >
          {saving ? (
            <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden="true" />
          ) : source === 'user' ? (
            <StatusDot tone="neutral" shape="square" label="Set by you" />
          ) : null}
          <span className="min-w-0 truncate">{saving ? 'Saving…' : label}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        </button>
      );
    } else {
      typeTag = (
        <span title={fullPath} className={[typeBase, TONE_CARD_STATIC].join(' ')} data-testid="category-chip-type">
          {source === 'user' && <StatusDot tone="neutral" shape="square" label="Set by you" />}
          <span className="min-w-0 truncate">{label}</span>
        </span>
      );
    }
  }

  return (
    <div
      role="group"
      aria-label={groupLabel}
      aria-busy={isBusy || undefined}
      className={[groupWrapClass, GROUP_BASE, className].filter(Boolean).join(' ')}
      data-testid="category-chip"
    >
      {scopeTag}
      {typeTag}
      {parentLabel && <span className="w-full text-xs text-muted-foreground">{parentLabel}</span>}
    </div>
  );
});

export default CategoryChip;
